/**
 * Confirmation d'identité avant une action sensible.
 *
 * Le hook `useSensitiveAction` encapsule le schéma complet : on tente l'action,
 * et si le serveur répond `reauthentication_required`, la modale s'ouvre, puis
 * l'action est rejouée automatiquement après confirmation. Les écrans n'ont
 * donc pas à gérer ce cas un par un.
 */

import { useCallback, useRef, useState } from "react";

import { ApiError } from "../api/client";
import { authApi } from "../api/endpoints";
import { useSession } from "../hooks/SessionContext";
import { Modal } from "./Modal";
import { Alert, Button, TextField } from "./ui";

export interface SensitiveActionState {
  /** Lance l'action, en demandant une confirmation d'identité si nécessaire. */
  run: () => Promise<void>;
  pending: boolean;
  dialog: React.ReactNode;
}

export function useSensitiveAction(
  action: () => Promise<void>,
  options: { onError?: (error: ApiError) => void } = {},
): SensitiveActionState {
  const { user, refresh } = useSession();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<ApiError | null>(null);
  const actionRef = useRef(action);
  actionRef.current = action;
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const needsCode = Boolean(user?.has_twofa);
  const methods = user?.twofa_methods.filter((m) => m.enabled) ?? [];
  const preferredMethod =
    methods.find((m) => m.method_type === "totp")?.method_type ??
    methods[0]?.method_type ??
    "totp";

  const attempt = useCallback(async () => {
    setPending(true);
    try {
      await actionRef.current();
      return true;
    } catch (caught) {
      if (caught instanceof ApiError && caught.requiresReauth) {
        setError(null);
        setOpen(true);
        return false;
      }
      if (caught instanceof ApiError) optionsRef.current.onError?.(caught);
      else throw caught;
      return false;
    } finally {
      setPending(false);
    }
  }, []);

  const confirm = useCallback(async () => {
    setPending(true);
    setError(null);
    try {
      await authApi.reauthenticate({
        password,
        twofa_code: needsCode ? code.trim() : undefined,
        twofa_method: needsCode ? preferredMethod : undefined,
      });
      await refresh();
      setOpen(false);
      setPassword("");
      setCode("");
      await actionRef.current();
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught);
        if (!caught.requiresReauth && caught.code !== "validation_error") {
          optionsRef.current.onError?.(caught);
        }
      } else {
        throw caught;
      }
    } finally {
      setPending(false);
    }
  }, [password, code, needsCode, preferredMethod, refresh]);

  const dialog = (
    <Modal
      open={open}
      title="Confirmez votre identité"
      description={
        <p>
          Cette action touche l'ensemble de vos données. Pour la réaliser, saisissez
          votre mot de passe
          {needsCode ? " et un code de vérification" : ""}.
        </p>
      }
      onClose={() => {
        setOpen(false);
        setError(null);
      }}
      actions={
        <Button
          variant="primary"
          loading={pending}
          onClick={() => void confirm()}
          disabled={!password || (needsCode && !code.trim())}
        >
          Confirmer
        </Button>
      }
    >
      {error && (
        <Alert tone="critical" title="Confirmation refusée">
          {error.message}
        </Alert>
      )}
      <form
        className="mt-4"
        onSubmit={(event) => {
          event.preventDefault();
          void confirm();
        }}
      >
        <TextField
          label="Mot de passe"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
        />
        {needsCode && (
          <TextField
            label={
              preferredMethod === "totp"
                ? "Code de votre application d'authentification"
                : "Code reçu par e-mail"
            }
            hint="Vous pouvez aussi utiliser un code de récupération."
            inputMode="text"
            autoComplete="one-time-code"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            required
          />
        )}
        {needsCode && methods.some((m) => m.method_type === "email") && (
          <Button
            type="button"
            variant="quiet"
            size="sm"
            onClick={() => void authApi.sendTwoFactorCode()}
          >
            Envoyer un code par e-mail
          </Button>
        )}
      </form>
    </Modal>
  );

  return {
    run: async () => {
      await attempt();
    },
    pending,
    dialog,
  };
}

/** Confirmation par saisie d'un mot exact, pour les actions irréversibles. */
export function ConfirmPhraseModal({
  open,
  title,
  description,
  phrase,
  confirmLabel,
  danger,
  pending,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  description: React.ReactNode;
  phrase: string;
  confirmLabel: string;
  danger?: boolean;
  pending?: boolean;
  onCancel: () => void;
  onConfirm: (phrase: string) => void;
}) {
  const [value, setValue] = useState("");
  const matches = value.trim().toUpperCase() === phrase.toUpperCase();
  return (
    <Modal
      open={open}
      title={title}
      description={description}
      onClose={() => {
        setValue("");
        onCancel();
      }}
      actions={
        <Button
          variant={danger ? "danger" : "primary"}
          disabled={!matches}
          loading={pending}
          onClick={() => onConfirm(value.trim().toUpperCase())}
        >
          {confirmLabel}
        </Button>
      }
    >
      <TextField
        label={`Saisissez « ${phrase} » pour confirmer`}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        autoComplete="off"
        spellCheck={false}
      />
    </Modal>
  );
}
